/**
 * Types and functions for tessellating faces and other types into meshes. This code is performance
 * tuned so be careful in here.
 * @module
 */
import cdt2d from "cdt2d";
import { ensureDefined } from "common/utils/assertOfType";
import degreeToRadian from "common/utils/degreeToRadian";
import earcut from "earcut";
import type { Alloc } from "./alloc";
import { ArenaAlloc, HeapAlloc } from "./alloc";
import type { BRep, Extrusion, Face } from "./brep";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, WindingDir, ANGLE_EPSILON } from "./core";
import type { Mesh2, Mesh3, QuadMesh3 } from "./mesh";
import { mesh2New, mesh3New, quadMesh3New } from "./mesh";
import { offsetPolyLine2 } from "./offset";
import { planeByThreePoints, planeCopyReversed, planeFixNaN, planeNormal } from "./plane";
import type { Plane } from "./planeTypes";
import {
  polyLine2AABB2,
  polyLine2Copy,
  polyLine2EdgeCount,
  polyLine2IsClosed,
  polyLine2MakeRoundedRect,
  polyLine2SignedArea,
  polyLine2WindingDir,
} from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";
import { clampNeg1Pos1, pushArray } from "./utils";
import type { UVTransformation } from "./uv";
import { UVGeneration, applyUVs, uvIdentityXForm } from "./uv";
import type { Vec2 } from "./vec2";
import { vec3Dist, vec3DistSquared, vec3Len, vec3Dot } from "./vec3";

export const HEAP_ALLOC: HeapAlloc = new HeapAlloc();

// 1 << 25 = 32MB
export const ARENA_ALLOC: ArenaAlloc = new ArenaAlloc(new ArrayBuffer(1 << 25));

export let alloc: Alloc = HEAP_ALLOC;

export function tessSetAlloc(value: Alloc) {
  alloc = value;
}

function allocFloat32(n: number) {
  return alloc.allocFloat32(n);
}

function allocUint32(n: number) {
  return alloc.allocUint32(n);
}

export function tessPolyLine2(loop: PolyLine2, id?: number): Mesh2 {
  return tessLoops2([loop], id);
}

export enum TriangulationStrategy {
  EARCUT,
  CONSTRAINED_DELAUNAY,
}

/**
 * @param face Note that the outer loop must have CW winding direction, and the rest of the loops must have CCW winding direction.
 * @param id
 * @param strategy
 * @param edgeGeometryDistance If defined, the edges of the extrusion get extra vertices for an edge wear effect. The extra vertices are offset inward from the face edges.
 * @returns The 2D tessellated mesh.
 */
export function tessFace2(
  face: Face,
  id: number = 0,
  strategy: TriangulationStrategy = TriangulationStrategy.EARCUT,
  edgeGeometryDistance: number | undefined = undefined,
): Mesh2 {
  return tessLoops2(face.loops, id, strategy, edgeGeometryDistance);
}

/**
 * @param face Note that the outer loop must have CW winding direction, and the rest of the loops must have CCW winding direction.
 * @param id
 * @param strategy
 * @param edgeGeometryDistance If defined, the edges of the extrusion get extra vertices for an edge wear effect. The extra vertices are offset inward from the face edges.
 * @returns The 2D tessellated mesh.
 */
function tessLoops2(
  loops: PolyLine2[],
  id: number = 0,
  strategy: TriangulationStrategy = TriangulationStrategy.EARCUT,
  edgeGeometryDistance: number | undefined = undefined,
): Mesh2 {
  const outerLoop = loops[0];

  const signedArea = outerLoop ? polyLine2SignedArea(outerLoop) : 0;

  if (!outerLoop || outerLoop.length < 8 || Math.abs(signedArea) < DIST_EPSILON || isNaN(signedArea)) {
    // invalid case
    return mesh2New(new Float32Array(0), new Uint32Array(0), new Uint32Array([id]));
  }

  console.assert(signedArea > 0, "Outer loop must have CW winding direction");

  const generateEdgeGeometry = edgeGeometryDistance !== undefined;

  // shortcut for common cases
  if (loops.length === 1 && !generateEdgeGeometry) {
    switch (outerLoop.length) {
      case 10: {
        return mesh2New(
          new Float32Array(outerLoop.slice(0, 8)),
          new Uint32Array([0, 2, 1, 0, 3, 2]),
          new Uint32Array([id]),
        );
      }
      case 8: {
        return mesh2New(new Float32Array(outerLoop.slice(0, 6)), new Uint32Array([0, 2, 1]), new Uint32Array([id]));
      }
    }
  }

  const innerLoops = loops.slice(1);

  let triangulationOuterLoop = outerLoop;
  let triangulationInnerLoops = innerLoops;
  if (generateEdgeGeometry) {
    // If we're generating edge geometry, the triangulation is based on loops that have been offset inward.
    triangulationOuterLoop = offsetPolyLine2(polyLine2Copy(outerLoop), edgeGeometryDistance);
    triangulationInnerLoops = innerLoops.map((x) => {
      console.assert(polyLine2WindingDir(x) === WindingDir.CCW);
      return offsetPolyLine2(polyLine2Copy(x), edgeGeometryDistance);
    });
  }

  // earcut/cdt2d assume polylines do not have trailing copy of first point
  const boundary = triangulationOuterLoop.slice(0, triangulationOuterLoop.length - 2);
  const holes = triangulationInnerLoops.map((x) => x.slice(0, x.length - 2));

  // count all of the points
  const holeIndices: number[] = [];
  let totalLoopCoordCount = boundary.length;
  for (const x of holes) {
    holeIndices.push(totalLoopCoordCount / 2);
    totalLoopCoordCount += x.length;
  }

  // merge all points into one array
  const allPts = allocFloat32(generateEdgeGeometry ? totalLoopCoordCount * 2 : totalLoopCoordCount);
  allPts.set(boundary, 0);
  let allPtsIndex = boundary.length;
  for (const x of holes) {
    allPts.set(x, allPtsIndex);
    allPtsIndex += x.length;
  }

  // triangulate
  let tess: number[] = [];
  switch (strategy) {
    case TriangulationStrategy.CONSTRAINED_DELAUNAY:
      // cdt2d expects an array of points, not a flattened one
      const points = allPts.subarray(0, totalLoopCoordCount).reduce((result, _value, index, array) => {
        if (index % 2 === 0) {
          result.push(array.slice(index, index + 2));
        }
        return result;
      }, [] as Float32Array[]);

      // cdt2d expects all edges to be enumerated in pairs
      const edges: Vec2[] = [];
      const enumerateEdges = (startIndex: number, exclusiveEndIndex: number) => {
        const count = exclusiveEndIndex - startIndex;
        for (let i = 0; i < count; i++) {
          edges.push([startIndex + i, startIndex + ((i + 1) % count)]);
        }
      };

      enumerateEdges(0, boundary.length / 2);
      for (let holeIndex = 0; holeIndex < holeIndices.length; holeIndex++) {
        const startIndex = holeIndices[holeIndex];
        const exclusiveEndIndex =
          holeIndex + 1 < holeIndices.length ? holeIndices[holeIndex + 1] : totalLoopCoordCount / 2;
        enumerateEdges(startIndex, exclusiveEndIndex);
      }

      try {
        const result = cdt2d(points, edges, { exterior: false });
        for (const indices of result) {
          pushArray(tess, indices);
        }
      } catch (e) {
        // cdt2d can fail on degenerate polygons (e.g., very short edges creating spikes/notches).
        // Fall back to earcut which is more robust for these cases.
        console.assert(false, "cdt2d failed, falling back to earcut:", e);
        tess = earcut(allPts.subarray(0, totalLoopCoordCount), holeIndices);
      }
      break;
    case TriangulationStrategy.EARCUT:
    default:
      tess = earcut(allPts.subarray(0, totalLoopCoordCount), holeIndices);
      break;
  }

  if (generateEdgeGeometry) {
    // Add the points at the edges that have not been offset and connect them to the offset points.
    // The first half of the buffer contains the offset points, the second half contains the corresponding points that have not been offset.
    const edgeIndexOffset = totalLoopCoordCount / 2; // Divide by 2 to get number of points from number of 2-vector coordinates.

    function addLoopEdgeTris(indexOffset: number, loopLen: number) {
      for (let firstVertIndex = 0; firstVertIndex < loopLen; ++firstVertIndex) {
        let secondVertIndex = firstVertIndex + 1;
        if (secondVertIndex === loopLen) {
          secondVertIndex = 0;
        }
        tess.push(
          indexOffset + firstVertIndex,
          indexOffset + secondVertIndex,
          indexOffset + edgeIndexOffset + secondVertIndex,
        );
        tess.push(
          indexOffset + firstVertIndex,
          indexOffset + edgeIndexOffset + secondVertIndex,
          indexOffset + edgeIndexOffset + firstVertIndex,
        );
      }
    }

    const loopNumPts = (outerLoop.length - 2) / 2;
    allPts.set(outerLoop.slice(0, outerLoop.length - 2), allPtsIndex);
    allPtsIndex += loopNumPts * 2;
    addLoopEdgeTris(0, loopNumPts);
    let indexOffset = loopNumPts;
    for (
      let innerLoopIndex = 0, innerLoopsCount = innerLoops.length;
      innerLoopIndex < innerLoopsCount;
      ++innerLoopIndex
    ) {
      const innerLoop = innerLoops[innerLoopIndex];
      const innerLoopEdgePts = innerLoop.slice(0, innerLoop.length - 2);
      allPts.set(innerLoopEdgePts, allPtsIndex);
      allPtsIndex += innerLoopEdgePts.length;
      const innerLoopNumPts = innerLoopEdgePts.length / 2;
      addLoopEdgeTris(indexOffset, innerLoopNumPts);
      indexOffset += innerLoopNumPts;
    }
  }

  const tris = allocUint32(tess.length);

  tris.set(tess);
  const ids = allocUint32(1);
  ids[0] = id;
  return mesh2New(allPts, tris, ids);
}

export function pointsOnPlane(
  pl: ArrayLike<number>,
  { originX, originY, originZ, uX, uY, uZ, vX, vY, vZ }: Plane,
): Float32Array {
  const res = allocFloat32((pl.length / 2) * 3);

  for (let i = 0, l = pl.length / 2; i < l; i++) {
    const i2 = 2 * i;
    const i3 = 3 * i;

    const u = pl[i2];
    const v = pl[i2 + 1];

    res[i3] = originX + uX * u + vX * v;
    res[i3 + 1] = originY + uY * u + vY * v;
    res[i3 + 2] = originZ + uZ * u + vZ * v;
  }

  return res;
}

export function tessFace3(
  f: Face,
  id?: number,
  uvGen: UVGeneration = UVGeneration.NONE,
  triangulationStrategy = TriangulationStrategy.EARCUT,
  xform?: UVTransformation,
): Mesh3 {
  return mesh2ToMesh3(tessFace2(f, id, triangulationStrategy), f.plane, uvGen, xform);
}

export function mesh2ToMesh3(
  mesh2: Mesh2,
  plane: Plane,
  uvGen: UVGeneration = UVGeneration.NONE,
  xform?: UVTransformation,
): Mesh3 {
  const pl = pointsOnPlane(mesh2.verts, plane);

  const [faceNormX, faceNormY, faceNormZ] = planeNormal(plane);

  const norms = allocFloat32(pl.length);
  for (let i = 0, l = pl.length; i < l; i += 3) {
    norms[i] = faceNormX;
    norms[i + 1] = faceNormY;
    norms[i + 2] = faceNormZ;
  }

  const uvs = allocFloat32(mesh2.verts.length);
  uvs.set(mesh2.verts);

  applyUVs(uvs, pl, plane, uvGen, xform);

  return mesh3New(pl, mesh2.tris, norms, uvs, undefined, mesh2.ids);
}

/**
 * @returns A list of all vertices in the given faces.
 */
export function vertexArrayFromFaces(faces: Face[]): number[] {
  const vertexArray: number[] = [];
  for (const face of faces) {
    const { originX, originY, originZ, uX, uY, uZ, vX, vY, vZ } = face.plane;
    for (const loop of face.loops) {
      const pl = loop;
      const l = polyLine2IsClosed(pl) ? pl.length / 2 - 1 : pl.length / 2;
      for (let i = 0; i < l; i++) {
        const u = pl[i * 2];
        const v = pl[i * 2 + 1];

        vertexArray.push(originX + uX * u + vX * v);
        vertexArray.push(originY + uY * u + vY * v);
        vertexArray.push(originZ + uZ * u + vZ * v);
      }
    }
  }
  return vertexArray;
}

/**
 * @param faces
 * @param id In case this in array, the length should equal to the length of the faces array. In case it's not an array, all the submeshes will share the same id.
 * @param globalRectUVs
 * @returns The generated mesh.
 */
export function tessFaces3(
  faces: Face[],
  id: number | number[] | (number | undefined)[] = 0,
  globalRectUVs: UVGeneration = UVGeneration.NONE,
  triangulationStrategy = TriangulationStrategy.EARCUT,
  xform?: UVTransformation | UVTransformation[] | (UVTransformation | undefined)[],
): Mesh3 {
  // prevent creation of excess garbage by allocating memory from arena
  tessSetAlloc(ARENA_ALLOC);

  let mesh2s;
  if (typeof id === "number") {
    mesh2s = faces.map((x) => tessFace2(x, undefined, triangulationStrategy));
  } else {
    console.assert(id.length === faces.length);
    mesh2s = [];
    let i = 0;
    for (const face of faces) {
      mesh2s.push(tessFace2(face, id[i], triangulationStrategy));
      ++i;
    }
  }

  let mesh3s: Mesh3[];
  if (Array.isArray(xform)) {
    console.assert(xform.length === faces.length);
    mesh3s = mesh2s
      .filter((x) => !x.verts.includes(NaN))
      .map((x, i) => mesh2ToMesh3(x, faces[i].plane, globalRectUVs, xform[i]))
      .filter((x) => !x.verts.includes(NaN));
  } else {
    mesh3s = mesh2s
      .filter((x) => !x.verts.includes(NaN))
      .map((x, i) => mesh2ToMesh3(x, faces[i].plane, globalRectUVs, xform))
      .filter((x) => !x.verts.includes(NaN));
  }

  // the remaining memory comes from the gc'ed heap
  ARENA_ALLOC.reset();
  tessSetAlloc(HEAP_ALLOC);

  let indexSize = 0;
  let vertSize = 0;
  for (let i = 0, l = mesh3s.length; i < l; i++) {
    const mesh = mesh3s[i];
    indexSize += mesh.tris.length;
    vertSize += mesh.verts.length;
  }

  const resTris = allocUint32(indexSize);
  const resVerts = allocFloat32(vertSize);
  const resNorms = allocFloat32(vertSize);
  const resUvs1 = allocFloat32((vertSize * 2) / 3);

  let indexCursor = 0;
  let vertCursor = 0;

  for (const mesh of mesh3s) {
    const { verts, norms, tris, uvs1 } = mesh;
    const offset = vertCursor / 3;

    // set pts, norms, uvs
    resVerts.set(verts, vertCursor);
    if (norms) {
      resNorms.set(norms, vertCursor);
    }
    if (uvs1) {
      resUvs1.set(uvs1, offset * 2);
    }
    vertCursor += verts.length;

    // update tris w/ offset
    for (let i = 0, l = tris.length; i < l; i++) {
      tris[i] += offset;
    }
    resTris.set(tris, indexCursor);

    indexCursor += tris.length;
  }

  let resIds: Uint32Array;
  if (typeof id === "number") {
    resIds = allocUint32(1);
    resIds[0] = id;
  } else {
    resIds = allocUint32(vertSize / 3);
    let idCursor = 0;
    for (const mesh of mesh3s) {
      const { ids, verts } = mesh;
      if (ids) {
        for (let i = 0, l = verts.length / 3; i < l; ++i) {
          // Each mesh should only have a single id here, since we just created them based on the faces.
          resIds[idCursor] = ids[0];
          ++idCursor;
        }
      }
    }
  }

  // for (const v of resVerts) {
  //   console.assert(!isNaN(v));
  // }

  return mesh3New(resVerts, resTris, resNorms, resUvs1, undefined, resIds);
}

export function quadMesh3ToMesh3(input: QuadMesh3, forceCopyAllBuffers: boolean): Mesh3 {
  const quads = input.quads;
  if (quads.length === 0 && !forceCopyAllBuffers) {
    return mesh3New(input.verts, input.tris, input.norms, input.uvs1, input.uvs2, input.ids);
  }
  const quadCount = input.quads.length / 4;
  const newTris: Uint32Array = new Uint32Array(input.tris.length + quadCount * 2 * 3);
  newTris.set(input.tris, 0);
  let writeIndex = input.tris.length;
  for (let i = 0; i < quadCount; ++i) {
    newTris[writeIndex++] = quads[i * 4];
    newTris[writeIndex++] = quads[i * 4 + 1];
    newTris[writeIndex++] = quads[i * 4 + 2];
    newTris[writeIndex++] = quads[i * 4];
    newTris[writeIndex++] = quads[i * 4 + 2];
    newTris[writeIndex++] = quads[i * 4 + 3];
  }
  if (forceCopyAllBuffers) {
    return mesh3New(
      new Float32Array(input.verts),
      newTris,
      input.norms ? new Float32Array(input.norms) : undefined,
      input.uvs1 ? new Float32Array(input.uvs1) : undefined,
      input.uvs2 ? new Float32Array(input.uvs2) : undefined,
      input.ids ? new Uint32Array(input.ids) : undefined,
    );
  } else {
    return mesh3New(input.verts, newTris, input.norms, input.uvs1, input.uvs2, input.ids);
  }
}

/**
 * @param ext
 * @param id
 * @param uvGen
 * @param edgeGeometryDistance If defined, the edges of the extrusion get extra vertices for an edge wear effect.
 * @returns The generated mesh.
 */
export function tessExtrusion(
  ext: Extrusion,
  id: number = 0,
  uvGen: UVGeneration = UVGeneration.NONE,
  edgeGeometryDistance: number | undefined = undefined,
  xform?: UVTransformation,
): Mesh3 {
  return tessExtrusions([ext], id, uvGen, edgeGeometryDistance, xform);
}

/**
 * @param exts
 * @param id In case this in array, the length should equal to the length of the extrusions array. In case it's not an array, all the submeshes will share the same id.
 * @param uvGen
 * @param edgeGeometryDistance If defined, the edges of the extrusion get extra vertices for an edge wear effect.
 * @returns The generated mesh.
 */
export function tessExtrusions(
  exts: Extrusion[],
  id: number | number[] = 0,
  uvGen: UVGeneration = UVGeneration.NONE,
  edgeGeometryDistance: number | undefined = undefined,
  xform?: UVTransformation | UVTransformation[],
): Mesh3 {
  // prevent creation of excess garbage by allocating temporary data from arena
  tessSetAlloc(ARENA_ALLOC);

  // 1. tessellate all the profiles

  let uvsOnPlane = new Array<Float32Array>(exts.length);
  let bottomFaces = new Array<Mesh2>(exts.length);
  const noHeight = new Array<Boolean>(exts.length);
  for (let i = 0, l = bottomFaces.length; i < l; i++) {
    const ext = exts[i];

    bottomFaces[i] = tessFace2(ext.profile, undefined, TriangulationStrategy.EARCUT, edgeGeometryDistance);
    uvsOnPlane[i] = pointsOnPlane(bottomFaces[i].verts, ext.profile.plane);

    const loopBB = polyLine2AABB2(ext.profile.loops[0]);
    noHeight[i] = loopBB.minY === loopBB.maxY || loopBB.minX === loopBB.maxX;
  }

  // 1a. filter any degenerate or zero height profiles

  const extsFiltered: Extrusion[] = [];
  const idsFiltered: number[] = [];
  const bottomFacesFiltered: Mesh2[] = [];
  const uvsOnPlaneFiltered: Float32Array[] = [];

  for (let i = 0, l = bottomFaces.length; i < l; i++) {
    if (!bottomFaces[i].verts.includes(NaN) && !noHeight[i]) {
      extsFiltered.push(exts[i]);
      if (Array.isArray(id)) {
        idsFiltered.push(id[i]);
      }
      bottomFacesFiltered.push(bottomFaces[i]);
      uvsOnPlaneFiltered.push(uvsOnPlane[i]);
    }
  }

  exts = extsFiltered;
  if (Array.isArray(id)) {
    id = idsFiltered;
  }
  uvsOnPlane = uvsOnPlaneFiltered;
  bottomFaces = bottomFacesFiltered;

  // the remaining memory comes from the gc'ed heap
  ARENA_ALLOC.reset();
  tessSetAlloc(HEAP_ALLOC);

  // 2. count the number of triangles, points for all of the extrusions
  let triLength = 0;
  let ptsLength = 0;

  const generateEdgeGeometry = edgeGeometryDistance !== undefined;
  const trisPerEdge = generateEdgeGeometry ? 10 : 2;
  const ptsPerEdge = generateEdgeGeometry ? 8 : 4;

  for (let i = 0, l = bottomFaces.length; i < l; i++) {
    // add the number of triangles on the top and bottom
    const bottomFace = bottomFaces[i];
    triLength += 2 * bottomFace.tris.length;

    // add pts from top & bottom
    ptsLength += 2 * (bottomFace.verts.length / 2) * 3;

    // sum the number of triangles for the sides
    const profile = exts[i].profile;

    for (const loop of profile.loops) {
      const edgeCount = polyLine2EdgeCount(loop);
      triLength += trisPerEdge * 3 * edgeCount;
      ptsLength += ptsPerEdge * 3 * edgeCount;
    }
  }

  // 3. combine all of the tessellations into one single mesh
  const finalUVs = allocFloat32((ptsLength / 3) * 2);
  const finalVerts = allocFloat32(ptsLength);
  const finalNorms = allocFloat32(ptsLength);
  const finalTris = allocUint32(triLength);

  let finalIds: Uint32Array;
  if (typeof id === "number") {
    finalIds = allocUint32(1);
    finalIds[0] = id;
  } else {
    console.assert(id.length === exts.length);
    finalIds = allocUint32(ptsLength / 3);
    let finalIdIndex = 0;
    for (let i = 0, l = exts.length; i < l; i++) {
      const extId = id[i];

      // add vertices from top & bottom
      const topBottomFaceVerts = 2 * (bottomFaces[i].verts.length / 2);
      for (let i2 = 0; i2 < topBottomFaceVerts; ++i2) {
        finalIds[finalIdIndex] = extId;
        ++finalIdIndex;
      }

      // sum the number of vertices for the sides
      const profile = exts[i].profile;
      for (const loop of profile.loops) {
        const edgeVerts = 4 * polyLine2EdgeCount(loop);
        for (let i2 = 0; i2 < edgeVerts; ++i2) {
          finalIds[finalIdIndex] = extId;
          ++finalIdIndex;
        }
      }
    }
    console.assert(finalIdIndex === finalIds.length);
  }

  let offsetPts = 0;
  let offsetUVs = 0;
  let offsetTris = 0;

  // Final vertex buffer order:
  // The "inner" parts only exist if generateEdgeGeometry is true.
  // (Bottom face 0 inner) | Bottom face 0 edge | (Cap face 0 inner) | Cap face 0 edge | (Side face 0A inner) | Side face 0A edge | (Side face 0B inner) | Side face 0B edge

  for (let i = 0, l = bottomFaces.length; i < l; i++) {
    const { tris, verts: uvs } = bottomFaces[i];
    const { profile, dirX, dirY, dirZ } = exts[i];
    const extXform = Array.isArray(xform) ? xform[i] : xform;
    const verts3 = uvsOnPlane[i];
    const edgePtsOffset = generateEdgeGeometry ? verts3.length / 2 : 0;

    // offset for base pts

    let offsetPts3 = offsetPts / 3;
    for (let j = 0, lj = tris.length; j < lj; j++) {
      tris[j] += offsetPts3;
    }

    // reverse base tris

    for (let j = 0, lj = tris.length; j < lj; j += 3) {
      const t = tris[j];
      tris[j] = tris[j + 1];
      tris[j + 1] = t;
    }

    // set base tris

    finalTris.set(tris, offsetTris);
    offsetTris += tris.length;

    // set base pts/uvs/norms

    const offsetPtsBase = offsetPts;

    const [capNormX, capNormY, capNormZ] = planeNormal(profile.plane);
    const baseNormX = !isNaN(capNormX) ? -capNormX : 0;
    const baseNormY = -capNormY;
    const baseNormZ = -capNormZ;

    // norms

    for (let j = offsetPts, lj = offsetPts + verts3.length; j < lj; j += 3) {
      finalNorms[j] = baseNormX;
      finalNorms[j + 1] = baseNormY;
      finalNorms[j + 2] = baseNormZ;
    }

    finalVerts.set(verts3, offsetPts);
    offsetPts += verts3.length;

    applyUVs(uvs, verts3, planeFixNaN(planeCopyReversed(profile.plane)), uvGen, extXform);
    finalUVs.set(uvs, offsetUVs);
    offsetUVs += uvs.length;

    // translate end pts

    for (let j = 0, lj = verts3.length; j < lj; j += 3) {
      verts3[j] += dirX;
      verts3[j + 1] += dirY;
      verts3[j + 2] += dirZ;
    }

    // reverse cap tris

    for (let j = 0, lj = tris.length; j < lj; j += 3) {
      const t = tris[j];
      tris[j] = tris[j + 1];
      tris[j + 1] = t;
    }

    // offset cap tris

    offsetPts3 = verts3.length / 3;
    for (let j = 0, lj = tris.length; j < lj; j++) {
      tris[j] += offsetPts3;
    }

    // set cap pts/uvs/norms

    const offsetPtsCap = offsetPts;

    for (let j = offsetPts, lj = offsetPts + verts3.length; j < lj; j += 3) {
      finalNorms[j] = capNormX;
      finalNorms[j + 1] = capNormY;
      finalNorms[j + 2] = capNormZ;
    }

    finalVerts.set(verts3, offsetPts);
    offsetPts += verts3.length;

    applyUVs(uvs, verts3, profile.plane, uvGen, extXform);
    finalUVs.set(uvs, offsetUVs);
    offsetUVs += uvs.length;

    // set cap tris

    finalTris.set(tris, offsetTris);
    offsetTris += tris.length;

    // make the sides

    let offsetPtsBaseLoop = offsetPtsBase;
    let offsetPtsCapLoop = offsetPtsCap;

    const extrusionDistance = vec3Len(dirX, dirY, dirZ);

    for (const loop of profile.loops) {
      const curEdgeLen = 0;
      // the loop will not include the end vertex
      const verts3InLoop = ((loop.length - 2) / 2) * 3;

      for (let j = 0, lj = loop.length - 2; j < lj; j += 2) {
        // form two triangles (a planar quad between the top pts, bottom pts)
        const j2 = j / 2;
        const j3 = j2 * 3;

        // set tris

        const offsetPts3 = offsetPts / 3;

        const a = offsetPts3;
        const b = offsetPts3 + 1;
        const c = offsetPts3 + 2;
        const d = offsetPts3 + 3;

        finalTris[offsetTris] = a;
        finalTris[offsetTris + 1] = c;
        finalTris[offsetTris + 2] = b;
        finalTris[offsetTris + 3] = a;
        finalTris[offsetTris + 4] = d;
        finalTris[offsetTris + 5] = c;

        if (generateEdgeGeometry) {
          const e = offsetPts3 + 4;
          const f = offsetPts3 + 5;
          const g = offsetPts3 + 6;
          const h = offsetPts3 + 7;

          finalTris[offsetTris + 6] = e;
          finalTris[offsetTris + 7] = a;
          finalTris[offsetTris + 8] = b;
          finalTris[offsetTris + 9] = e;
          finalTris[offsetTris + 10] = b;
          finalTris[offsetTris + 11] = f;

          finalTris[offsetTris + 12] = f;
          finalTris[offsetTris + 13] = b;
          finalTris[offsetTris + 14] = c;
          finalTris[offsetTris + 15] = f;
          finalTris[offsetTris + 16] = c;
          finalTris[offsetTris + 17] = g;

          finalTris[offsetTris + 18] = g;
          finalTris[offsetTris + 19] = c;
          finalTris[offsetTris + 20] = d;
          finalTris[offsetTris + 21] = g;
          finalTris[offsetTris + 22] = d;
          finalTris[offsetTris + 23] = h;

          finalTris[offsetTris + 24] = h;
          finalTris[offsetTris + 25] = d;
          finalTris[offsetTris + 26] = a;
          finalTris[offsetTris + 27] = h;
          finalTris[offsetTris + 28] = a;
          finalTris[offsetTris + 29] = e;
        }

        offsetTris += trisPerEdge * 3;

        // look up pts from base and cap

        const a3 = offsetPtsBaseLoop + edgePtsOffset + (j3 % verts3InLoop);
        const b3 = offsetPtsBaseLoop + edgePtsOffset + ((j3 + 3) % verts3InLoop);

        const c3 = offsetPtsCapLoop + edgePtsOffset + ((j3 + 3) % verts3InLoop);
        const d3 = offsetPtsCapLoop + edgePtsOffset + (j3 % verts3InLoop);

        const aX = finalVerts[a3];
        const aY = finalVerts[a3 + 1];
        const aZ = finalVerts[a3 + 2];

        const bX = finalVerts[b3];
        const bY = finalVerts[b3 + 1];
        const bZ = finalVerts[b3 + 2];

        const cX = finalVerts[c3];
        const cY = finalVerts[c3 + 1];
        const cZ = finalVerts[c3 + 2];

        const dX = finalVerts[d3];
        const dY = finalVerts[d3 + 1];
        const dZ = finalVerts[d3 + 2];

        const flattenVec3sToArray = (a: Float32Array, startIndex: number, v: number[][]) => {
          console.assert(a.length >= startIndex + v.length * 3);
          for (const vec3 of v) {
            a[startIndex] = vec3[0];
            a[startIndex + 1] = vec3[1];
            a[startIndex + 2] = vec3[2];
            startIndex += 3;
          }
        };

        const uniqueCorners = [aX, aY, aZ, cX, cY, cZ, bX, bY, bZ];
        const distACsquared = vec3DistSquared(aX, aY, aZ, cX, cY, cZ);
        if (distACsquared < DIST_EPSILON_SQUARED) {
          // First two points coincide, replace the second with the fourth.
          uniqueCorners[3] = dX;
          uniqueCorners[4] = dY;
          uniqueCorners[5] = dZ;
        } else {
          const distCBsquared = vec3DistSquared(cX, cY, cZ, bX, bY, bZ);
          const dotACBC = vec3Dot(aX - cX, aY - cY, aZ - cZ, bX - cX, bY - cY, bZ - cZ);
          const cosAngleSquared = (dotACBC * dotACBC) / (distACsquared * distCBsquared);
          if (cosAngleSquared < ANGLE_EPSILON) {
            // First three points are colinear, replace the third with the fourth.
            uniqueCorners[6] = dX;
            uniqueCorners[7] = dY;
            uniqueCorners[8] = dZ;
          }
        }

        const quadPlane: Plane | null = planeByThreePoints(
          uniqueCorners[0],
          uniqueCorners[1],
          uniqueCorners[2],
          uniqueCorners[3],
          uniqueCorners[4],
          uniqueCorners[5],
          uniqueCorners[6],
          uniqueCorners[7],
          uniqueCorners[8],
        );

        // Still can be degenerate if all 4 points were colinear.
        if (!quadPlane) {
          // console.warn("Degenerate quad detected!");
          continue;
        }

        const norm = planeNormal(quadPlane);

        const processSideVerts = (v: number[][], xTOffset: number, yTOffset: number) => {
          // set verts

          flattenVec3sToArray(finalVerts, offsetPts, v);

          // set normals

          flattenVec3sToArray(finalNorms, offsetPts, [norm, norm, norm, norm]);

          // Set uvs!

          if (uvGen !== UVGeneration.NONE) {
            applyUVs(finalUVs, finalVerts, quadPlane, uvGen, extXform, offsetUVs, offsetUVs + 8, offsetPts);
          } else {
            const nextEdgeLen = curEdgeLen + vec3Dist(aX, aY, aZ, bX, bY, bZ);

            // The UVs are offset inward here
            finalUVs[offsetUVs] = curEdgeLen * (1 - xTOffset) + nextEdgeLen * xTOffset;
            finalUVs[offsetUVs + 1] = extrusionDistance * yTOffset;

            finalUVs[offsetUVs + 2] = nextEdgeLen * (1 - xTOffset) + curEdgeLen * xTOffset;
            finalUVs[offsetUVs + 3] = extrusionDistance * yTOffset;

            finalUVs[offsetUVs + 4] = nextEdgeLen * (1 - xTOffset) + curEdgeLen * xTOffset;
            finalUVs[offsetUVs + 5] = extrusionDistance * (1 - yTOffset);

            finalUVs[offsetUVs + 6] = curEdgeLen * (1 - xTOffset) + nextEdgeLen * xTOffset;
            finalUVs[offsetUVs + 7] = extrusionDistance * (1 - yTOffset);
          }

          offsetPts += 12;
          offsetUVs += 8;
        };

        if (generateEdgeGeometry) {
          // Generate inner points for edge geometry

          function interpolateRect(xT: number, yT: number) {
            return [
              (aX * (1 - xT) + bX * xT) * (1 - yT) + (dX * (1 - xT) + cX * xT) * yT,
              (aY * (1 - xT) + bY * xT) * (1 - yT) + (dY * (1 - xT) + cY * xT) * yT,
              (aZ * (1 - xT) + bZ * xT) * (1 - yT) + (dZ * (1 - xT) + cZ * xT) * yT,
            ];
          }

          const xTOffset = edgeGeometryDistance / vec3Dist(aX, aY, aZ, bX, bY, bZ);
          const yTOffset = edgeGeometryDistance / vec3Dist(aX, aY, aZ, dX, dY, dZ);

          const topLeft = interpolateRect(xTOffset, yTOffset);
          const topRight = interpolateRect(1 - xTOffset, yTOffset);
          const bottomRight = interpolateRect(1 - xTOffset, 1 - yTOffset);
          const bottomLeft = interpolateRect(xTOffset, 1 - yTOffset);

          processSideVerts([topLeft, topRight, bottomRight, bottomLeft], xTOffset, yTOffset);
        }

        processSideVerts(
          [
            [aX, aY, aZ],
            [bX, bY, bZ],
            [cX, cY, cZ],
            [dX, dY, dZ],
          ],
          0,
          0,
        );
      }

      offsetPtsBaseLoop += verts3InLoop;
      offsetPtsCapLoop += verts3InLoop;
    }
  }

  // for (const v of finalVerts) {
  //   console.assert(!isNaN(v));
  // }

  return {
    tris: finalTris,
    verts: finalVerts,
    norms: finalNorms,
    uvs1: finalUVs,
    ids: finalIds,
  };
}

/**
 * @param breps
 * @param id In case this in array, the length should equal to the length of the breps array. In case it's not an array, all the submeshes will share the same id.
 * @param globalRectUVs
 * @returns The generated mesh.
 */
export function tessBreps(
  breps: BRep[],
  id: number | number[] = 0,
  globalRectUVs: UVGeneration = UVGeneration.NONE,
  xform: UVTransformation | UVTransformation[] = uvIdentityXForm,
): Mesh3 {
  const faces: Face[] = [];
  if (typeof id === "number" && !Array.isArray(xform)) {
    for (let i = 0, l = breps.length; i < l; i++) {
      pushArray(faces, breps[i].faces);
    }

    return tessFaces3(faces, id, globalRectUVs, TriangulationStrategy.EARCUT, xform);
  } else {
    id = typeof id === "number" ? new Array<number>(breps.length).fill(id) : id;
    xform = Array.isArray(xform) ? xform : new Array<UVTransformation>(breps.length).fill(xform);

    console.assert(id.length === breps.length);
    console.assert(xform.length === breps.length);

    // We need to make the number of ids equal to the number of faces.
    const idsExpanded: number[] = [];
    const xformsExpanded: UVTransformation[] = [];

    for (let i = 0, l = breps.length; i < l; i++) {
      pushArray(faces, breps[i].faces);
      for (let i2 = 0, l2 = breps[i].faces.length; i2 < l2; i2++) {
        idsExpanded.push(id[i]);
        xformsExpanded.push(xform[i]);
      }
    }

    return tessFaces3(faces, idsExpanded, globalRectUVs, TriangulationStrategy.EARCUT, xformsExpanded);
  }
}

export function tessBRep(
  brep: BRep,
  id: number = 0,
  globalRectUVs: UVGeneration = UVGeneration.NONE,
  xform?: UVTransformation,
): Mesh3 {
  return tessFaces3(brep.faces, id, globalRectUVs, TriangulationStrategy.EARCUT, xform);
}

/**
 * Generate clean geometry for a rounded rectangle made out of quads and tris. The geometry will be suitable for further subdivision.
 * The geometry will be centered at the origin and oriented so that the extrusion is done along the z axis.
 * The rounding radius can be greater than half of the x or y size of the rectangle. In this case, the clipping matches the clipping done by the SDF shader.
 * The function can return undefined in case the mesh is entirely clipped out. */
export function tessExtrudedRoundedRectangleAsQuads(
  xSize: number,
  ySize: number,
  extrusionDistance: number,
  roundingRadius: number,
  maxStepDegrees: number,
): QuadMesh3 | undefined {
  const loopAttempt = polyLine2MakeRoundedRect(
    -xSize * 0.5,
    -ySize * 0.5,
    xSize * 0.5,
    ySize * 0.5,
    roundingRadius,
    maxStepDegrees,
  );
  if (loopAttempt === undefined) {
    return undefined;
  }

  const loop: PolyLine2 = ensureDefined(loopAttempt);

  const verts: number[] = [];
  const tris: number[] = [];
  const quads: number[] = [];

  const zBottom = -extrusionDistance * 0.5;
  const zTop = extrusionDistance * 0.5;

  // To get the actual vertex index in the bottom of the mesh, double this index. To get the top vertex, double and add one.
  const cardinalEdgeStartingVertIndices: number[] = [];

  let prevX: number = 0;
  let prevY: number = 0;
  // Add all vertices around the edges and the vertical quads.
  for (let i = 0, l = loop.length / 2 - 1; i < l; ++i) {
    const x = loop[i * 2];
    const y = loop[i * 2 + 1];
    verts.push(x, y, zBottom);
    verts.push(x, y, zTop);
    if (i > 0) {
      quads.push((i - 1) * 2, i * 2, i * 2 + 1, (i - 1) * 2 + 1);
      if (x === prevX || y === prevY) {
        cardinalEdgeStartingVertIndices.push(i - 1);
      }
    }
    prevX = x;
    prevY = y;
  }
  // Close the loop of vertical quads.
  const rimVertexCount = verts.length / 3;
  quads.push(rimVertexCount - 2, 0, 1, rimVertexCount - 1);

  function getLoopVertexWithWrap(ind: number): number[] {
    return [loop[(ind * 2) % loop.length], loop[(ind * 2 + 1) % loop.length]];
  }

  const lastVert = getLoopVertexWithWrap(rimVertexCount / 2 - 1);
  const firstVert = getLoopVertexWithWrap(0);
  if (lastVert[0] === firstVert[0] || lastVert[1] === firstVert[1]) {
    cardinalEdgeStartingVertIndices.push(rimVertexCount / 2 - 1);
  }

  // Check if the rounding quarter circles clip each other.
  let clippedX = false;
  let clippedY = false;
  if (ySize * 0.5 < roundingRadius) {
    clippedY = true;
  }
  if (xSize * 0.5 < roundingRadius) {
    clippedX = true;
  }

  // Vertex indices for the rounded corner centers. If the rounded sections clip each other, then some of these corners share the same vertex.
  // To get the actual vertex index in the bottom of the mesh, double this index. To get the top vertex, double and add one.
  let posXposYCornerCenterIndex: number,
    negXposYCornerCenterIndex: number,
    posXnegYCornerCenterIndex: number,
    negXnegYCornerCenterIndex: number;

  // All input indices are half of the actual bottom vertex index.
  function pushCapQuads(i0: number, i1: number, i2: number, i3: number): void {
    // Reverse winding order for the bottom quad
    quads.push(i0 * 2, i3 * 2, i2 * 2, i1 * 2);
    // Top quad
    quads.push(i0 * 2 + 1, i1 * 2 + 1, i2 * 2 + 1, i3 * 2 + 1);
  }
  function pushCapTris(i0: number, i1: number, i2: number): void {
    // Reverse winding order for the bottom tri
    tris.push(i0 * 2, i2 * 2, i1 * 2);
    // Top tri
    tris.push(i0 * 2 + 1, i1 * 2 + 1, i2 * 2 + 1);
  }

  // Add the vertices and quads for the caps.
  if (!clippedX && !clippedY) {
    const x = xSize * 0.5 - roundingRadius;
    const y = ySize * 0.5 - roundingRadius;
    posXposYCornerCenterIndex = verts.length / 6;
    verts.push(x, y, zBottom);
    verts.push(x, y, zTop);
    negXposYCornerCenterIndex = verts.length / 6;
    verts.push(-x, y, zBottom);
    verts.push(-x, y, zTop);
    posXnegYCornerCenterIndex = verts.length / 6;
    verts.push(x, -y, zBottom);
    verts.push(x, -y, zTop);
    negXnegYCornerCenterIndex = verts.length / 6;
    verts.push(-x, -y, zBottom);
    verts.push(-x, -y, zTop);

    // Center quad of the cap.
    pushCapQuads(
      negXnegYCornerCenterIndex,
      posXnegYCornerCenterIndex,
      posXposYCornerCenterIndex,
      negXposYCornerCenterIndex,
    );
  } else if (clippedX && clippedY) {
    posXposYCornerCenterIndex = verts.length / 6;
    negXposYCornerCenterIndex = verts.length / 6;
    posXnegYCornerCenterIndex = verts.length / 6;
    negXnegYCornerCenterIndex = verts.length / 6;
    verts.push(0, 0, zBottom);
    verts.push(0, 0, zTop);
  } else if (clippedX) {
    const y = ySize * 0.5 - roundingRadius;
    posXposYCornerCenterIndex = verts.length / 6;
    negXposYCornerCenterIndex = verts.length / 6;
    verts.push(0, y, zBottom);
    verts.push(0, y, zTop);
    posXnegYCornerCenterIndex = verts.length / 6;
    negXnegYCornerCenterIndex = verts.length / 6;
    verts.push(0, -y, zBottom);
    verts.push(0, -y, zTop);
  } else {
    console.assert(clippedY && !clippedX);
    const x = xSize * 0.5 - roundingRadius;
    posXnegYCornerCenterIndex = verts.length / 6;
    posXposYCornerCenterIndex = verts.length / 6;
    verts.push(x, 0, zBottom);
    verts.push(x, 0, zTop);
    negXnegYCornerCenterIndex = verts.length / 6;
    negXposYCornerCenterIndex = verts.length / 6;
    verts.push(-x, 0, zBottom);
    verts.push(-x, 0, zTop);
  }

  for (const ind0 of cardinalEdgeStartingVertIndices) {
    const [x0, y0] = getLoopVertexWithWrap(ind0);
    const ind1 = (ind0 + 1) % (rimVertexCount / 2);
    const [x1, y1] = getLoopVertexWithWrap(ind1);
    console.assert(x0 === x1 || y0 === y1);
    if (y0 > 0 && y1 > 0) {
      if (x1 > x0) {
        pushCapQuads(negXposYCornerCenterIndex, posXposYCornerCenterIndex, ind1, ind0);
      } else {
        pushCapQuads(negXposYCornerCenterIndex, posXposYCornerCenterIndex, ind0, ind1);
      }
    } else if (y0 < 0 && y1 < 0) {
      if (x1 > x0) {
        pushCapQuads(ind0, ind1, posXnegYCornerCenterIndex, negXnegYCornerCenterIndex);
      } else {
        pushCapQuads(ind1, ind0, posXnegYCornerCenterIndex, negXnegYCornerCenterIndex);
      }
    } else if (x0 > 0) {
      console.assert(x1 === x0);
      if (y1 > y0) {
        pushCapQuads(posXnegYCornerCenterIndex, ind0, ind1, posXposYCornerCenterIndex);
      } else {
        pushCapQuads(posXnegYCornerCenterIndex, ind1, ind0, posXposYCornerCenterIndex);
      }
    } else {
      console.assert(x0 < 0 && x1 === x0);
      if (y1 > y0) {
        pushCapQuads(ind0, negXnegYCornerCenterIndex, negXposYCornerCenterIndex, ind1);
      } else {
        pushCapQuads(ind1, negXnegYCornerCenterIndex, negXposYCornerCenterIndex, ind0);
      }
    }
  }

  // Add the triangles in a fan formation for the rounded parts of the caps.
  const loopUniquePoints = loop.length / 2 - 1;
  prevX = loop[(loopUniquePoints - 1) * 2];
  prevY = loop[(loopUniquePoints - 1) * 2 + 1];
  for (let i = 0; i < loopUniquePoints; ++i) {
    const x = loop[i * 2];
    const y = loop[i * 2 + 1];
    if (x !== prevX && y !== prevY) {
      let cornerIndex = posXposYCornerCenterIndex;
      if (x < 0 && y < 0) {
        cornerIndex = negXnegYCornerCenterIndex;
      } else if (y < 0) {
        cornerIndex = posXnegYCornerCenterIndex;
      } else if (x < 0) {
        cornerIndex = negXposYCornerCenterIndex;
      }
      if (i > 0) {
        pushCapTris(i - 1, i, cornerIndex);
      } else {
        pushCapTris(loopUniquePoints - 1, i, cornerIndex);
      }
    }
    prevX = x;
    prevY = y;
  }

  return quadMesh3New(new Float32Array(verts), new Uint32Array(tris), new Uint32Array(quads));
}

export function tessRectangularPrismAsQuads(xSize: number, ySize: number, zSize: number): QuadMesh3 {
  const x = xSize * 0.5,
    y = ySize * 0.5,
    z = zSize * 0.5;
  const verts = [
    ...[-x, -y, -z],
    ...[x, -y, -z],
    ...[-x, y, -z],
    ...[x, y, -z],
    ...[-x, -y, z],
    ...[x, -y, z],
    ...[-x, y, z],
    ...[x, y, z],
  ];
  const quads = [...[0, 2, 3, 1], ...[4, 5, 7, 6], ...[0, 1, 5, 4], ...[3, 2, 6, 7], ...[1, 3, 7, 5], ...[2, 0, 4, 6]];
  return quadMesh3New(new Float32Array(verts), new Uint32Array([]), new Uint32Array(quads));
}

/**
 * The prism will be centered at the origin and rounded along all the edges.
 * The rounding radius can be greater than half of the x, y or z size of the rectangle. In this case, the clipping matches the clipping done by the SDF shader.
 * The function can return undefined in case the mesh is entirely clipped out. */
export function tessRoundedRectangularPrism(
  xSize: number,
  ySize: number,
  zSize: number,
  roundingRadius: number,
  generateQuadMesh: boolean,
  maxStepDegrees: number = 10,
): Mesh3 | QuadMesh3 | undefined {
  // Check if the rounding circles clip each other.
  let clippedX = false;
  let clippedY = false;
  let clippedZ = false;
  if (zSize * 0.5 < roundingRadius) {
    clippedZ = true;
  }
  if (ySize * 0.5 < roundingRadius) {
    clippedY = true;
  }
  if (xSize * 0.5 < roundingRadius) {
    clippedX = true;
  }

  const cornerVerts: number[] = [];
  const cornerTris: number[] = [];
  const cornerQuads: number[] = [];

  let longitudalEdgeCount;

  let startLatitude = 0.0;
  const endLatitude = Math.PI * 0.5;

  const clipZFromCircleCenter = roundingRadius - zSize * 0.5;

  if (clippedZ) {
    startLatitude = Math.asin(clipZFromCircleCenter / roundingRadius);
  }

  const maxAngleStepRad = degreeToRadian(maxStepDegrees + 0.001);
  const latitudeStepCount = Math.ceil((endLatitude - startLatitude) / maxAngleStepRad);
  const latitudeStepRad = (endLatitude - startLatitude) / latitudeStepCount;

  function addCornerQuad(i0: number, i1: number, i2: number, i3: number) {
    if (generateQuadMesh) {
      cornerQuads.push(i0);
      cornerQuads.push(i1);
      cornerQuads.push(i2);
      cornerQuads.push(i3);
    } else {
      cornerTris.push(i0);
      cornerTris.push(i1);
      cornerTris.push(i2);
      cornerTris.push(i0);
      cornerTris.push(i2);
      cornerTris.push(i3);
    }
  }

  // Tessellate the spherical part of the corner in the +x +y +z octant.
  const cornerSphereCenterX = xSize * 0.5 - roundingRadius;
  const cornerSphereCenterY = ySize * 0.5 - roundingRadius;
  const cornerSphereCenterZ = zSize * 0.5 - roundingRadius;
  let latitude = startLatitude;
  let latitudeIndex = 0;
  while (latitudeIndex < latitudeStepCount) {
    let z = Math.max(0, cornerSphereCenterZ + Math.sin(latitude) * roundingRadius);
    if (latitudeIndex === 0) {
      if (cornerSphereCenterZ <= 0) {
        // Handle numerical imprecision by forcing z to zero.
        z = 0;
      }
    }
    const loopRadius = Math.cos(latitude) * roundingRadius;

    if (
      (clippedX &&
        clippedY &&
        Math.pow(cornerSphereCenterX, 2) + Math.pow(cornerSphereCenterY, 2) >= Math.pow(loopRadius, 2)) ||
      (clippedY && -cornerSphereCenterY >= loopRadius) ||
      (clippedX && -cornerSphereCenterX >= loopRadius)
    ) {
      // The circle at this latitude is clipped out entirely.
      break;
    }

    let startLongitude = 0.0;
    let endLongitude = Math.PI * 0.5;

    // Compute start and end angles for the circular curve section.
    if (clippedY) {
      startLongitude = Math.asin(clampNeg1Pos1(-cornerSphereCenterY / loopRadius));
    }
    if (clippedX) {
      endLongitude = Math.acos(clampNeg1Pos1(-cornerSphereCenterX / loopRadius));
    }
    if (longitudalEdgeCount === undefined) {
      longitudalEdgeCount = Math.ceil((endLongitude - startLongitude) / maxAngleStepRad);
    }
    const longitudeStepRad = (endLongitude - startLongitude) / longitudalEdgeCount;
    let longitude = startLongitude;
    for (let i = 0; i < longitudalEdgeCount; ++i) {
      cornerVerts.push(
        cornerSphereCenterX + Math.cos(longitude) * loopRadius,
        cornerSphereCenterY + Math.sin(longitude) * loopRadius,
        z,
      );
      longitude += longitudeStepRad;
    }
    cornerVerts.push(
      cornerSphereCenterX + Math.cos(endLongitude) * loopRadius,
      cornerSphereCenterY + Math.sin(endLongitude) * loopRadius,
      z,
    );

    latitude += latitudeStepRad;
    if (latitudeIndex > 0) {
      const topRowStart = latitudeIndex * (longitudalEdgeCount + 1);
      const bottomRowStart = (latitudeIndex - 1) * (longitudalEdgeCount + 1);
      for (let i = 0; i < longitudalEdgeCount; ++i) {
        addCornerQuad(bottomRowStart + i, bottomRowStart + i + 1, topRowStart + i + 1, topRowStart + i);
      }
    }
    ++latitudeIndex;
  }

  if (longitudalEdgeCount === undefined || longitudalEdgeCount === 0) {
    // There was nothing left of the prism after applying the rounding radius.
    return undefined;
  }

  {
    // Add the north pole to the corner.
    const x = Math.max(0, cornerSphereCenterX);
    const y = Math.max(0, cornerSphereCenterY);
    // Solve z from:
    // z = cornerSphereCenterZ + zOffset
    // xOffset^2 + yOffset^2 + zOffset^2 = roundingRadius^2
    const z =
      cornerSphereCenterZ +
      Math.sqrt(
        Math.pow(roundingRadius, 2) - Math.pow(x - cornerSphereCenterX, 2) - Math.pow(y - cornerSphereCenterY, 2),
      );
    cornerVerts.push(x, y, z);
    const poleIndex = cornerVerts.length / 3 - 1;
    // Add the triangles connecting the last circle section row of vertices to the north pole of the corner.
    const bottomRowStart = (latitudeIndex - 1) * (longitudalEdgeCount + 1);
    for (let i = 0; i < longitudalEdgeCount; ++i) {
      cornerTris.push(bottomRowStart + i);
      cornerTris.push(bottomRowStart + i + 1);
      cornerTris.push(poleIndex);
    }
  }

  const verts: number[] = [];
  const tris: number[] = [];
  const quads: number[] = [];

  const cornerVertCount = cornerVerts.length / 3;
  const cornerTriCount = cornerTris.length / 3;
  const cornerQuadCount = cornerQuads.length / 4;

  // Generate all 8 corners of the prism by mirroring the positive corner.
  for (let cornerIndex = 0; cornerIndex < 8; ++cornerIndex) {
    const mirrorX = (cornerIndex & 1) !== 0;
    const mirrorY = (cornerIndex & 2) !== 0;
    const mirrorZ = (cornerIndex & 4) !== 0;

    for (let i = 0; i < cornerVertCount; ++i) {
      const x = (mirrorX ? -1 : 1) * cornerVerts[i * 3];
      const y = (mirrorY ? -1 : 1) * cornerVerts[i * 3 + 1];
      const z = (mirrorZ ? -1 : 1) * cornerVerts[i * 3 + 2];
      verts.push(x, y, z);
    }

    // If we've mirrored the vertices an odd number of times, we have to flip the winding order of the triangles.
    const flipWinding = ((mirrorX ? 1 : 0) + (mirrorY ? 1 : 0) + (mirrorZ ? 1 : 0)) % 2 === 1;

    for (let i = 0; i < cornerTriCount; ++i) {
      tris.push(cornerTris[i * 3] + cornerIndex * cornerVertCount);
      if (flipWinding) {
        tris.push(cornerTris[i * 3 + 2] + cornerIndex * cornerVertCount);
        tris.push(cornerTris[i * 3 + 1] + cornerIndex * cornerVertCount);
      } else {
        tris.push(cornerTris[i * 3 + 1] + cornerIndex * cornerVertCount);
        tris.push(cornerTris[i * 3 + 2] + cornerIndex * cornerVertCount);
      }
    }
    for (let i = 0; i < cornerQuadCount; ++i) {
      quads.push(cornerQuads[i * 4] + cornerIndex * cornerVertCount);
      if (flipWinding) {
        quads.push(cornerQuads[i * 4 + 3] + cornerIndex * cornerVertCount);
        quads.push(cornerQuads[i * 4 + 2] + cornerIndex * cornerVertCount);
        quads.push(cornerQuads[i * 4 + 1] + cornerIndex * cornerVertCount);
      } else {
        quads.push(cornerQuads[i * 4 + 1] + cornerIndex * cornerVertCount);
        quads.push(cornerQuads[i * 4 + 2] + cornerIndex * cornerVertCount);
        quads.push(cornerQuads[i * 4 + 3] + cornerIndex * cornerVertCount);
      }
    }
  }

  const vertsPerLatitude = longitudalEdgeCount + 1;

  // Get the index of a vertex at the vertical edge of one of the rounded corners.
  function getVerticalEdgeVertexIndex(edgeCloseToXCenter: boolean, prismCorner: number, i: number) {
    // Offset the index to the edge closer to x center if that's been requested.
    const offsetAtLatitude = edgeCloseToXCenter ? longitudalEdgeCount : 0;
    // Unlike other latitudes, the pole has just one vertex, we take that into account by simply clamping out-of-range offsets to cornerVertCount.
    return prismCorner * cornerVertCount + Math.min(i * vertsPerLatitude + offsetAtLatitude, cornerVertCount - 1);
  }

  function addTri(i0: number, i1: number, i2: number, flipWinding: boolean) {
    tris.push(i0);
    if (flipWinding) {
      tris.push(i2);
      tris.push(i1);
    } else {
      tris.push(i1);
      tris.push(i2);
    }
  }

  function addQuad(i0: number, i1: number, i2: number, i3: number, flipWinding: boolean) {
    if (generateQuadMesh) {
      quads.push(i0);
      if (flipWinding) {
        quads.push(i3);
        quads.push(i2);
        quads.push(i1);
      } else {
        quads.push(i1);
        quads.push(i2);
        quads.push(i3);
      }
    } else {
      addTri(i0, i1, i2, flipWinding);
      addTri(i0, i2, i3, flipWinding);
    }
  }

  // Fill in connecting sections in between the corners.
  if (!clippedX) {
    for (let startCornerIndex = 0; startCornerIndex < 8; startCornerIndex += 2) {
      const flipWinding = startCornerIndex === 2 || startCornerIndex === 4;
      for (let i = 0; i < latitudeIndex; ++i) {
        addQuad(
          getVerticalEdgeVertexIndex(true, startCornerIndex, i),
          getVerticalEdgeVertexIndex(true, startCornerIndex + 1, i),
          getVerticalEdgeVertexIndex(true, startCornerIndex + 1, i + 1),
          getVerticalEdgeVertexIndex(true, startCornerIndex, i + 1),
          flipWinding,
        );
      }
    }
  }

  if (!clippedY) {
    for (let ci = 0; ci < 4; ++ci) {
      const startCornerIndex = [0, 1, 4, 5][ci];
      const flipWinding = ci === 0 || ci === 3;
      for (let i = 0; i < latitudeIndex; ++i) {
        addQuad(
          getVerticalEdgeVertexIndex(false, startCornerIndex, i),
          getVerticalEdgeVertexIndex(false, startCornerIndex + 2, i),
          getVerticalEdgeVertexIndex(false, startCornerIndex + 2, i + 1),
          getVerticalEdgeVertexIndex(false, startCornerIndex, i + 1),
          flipWinding,
        );
      }
    }
  }

  if (!clippedZ) {
    for (let startCornerIndex = 0; startCornerIndex < 4; startCornerIndex += 1) {
      const flipWinding = startCornerIndex === 1 || startCornerIndex === 2;
      for (let i = 0; i < longitudalEdgeCount; ++i) {
        addQuad(
          cornerVertCount * startCornerIndex + i,
          cornerVertCount * (startCornerIndex + 4) + i,
          cornerVertCount * (startCornerIndex + 4) + i + 1,
          cornerVertCount * startCornerIndex + i + 1,
          flipWinding,
        );
      }
    }
  }

  if (!clippedX && !clippedY) {
    // Add a rectangular cap to the positive z and negative z faces of the prism.
    // This is done by connecting the poles of the corners.
    function xyCapVertIndex(prismCorner: number) {
      return prismCorner * cornerVertCount + cornerVertCount - 1;
    }
    addQuad(xyCapVertIndex(0), xyCapVertIndex(1), xyCapVertIndex(3), xyCapVertIndex(2), false);
    addQuad(xyCapVertIndex(4), xyCapVertIndex(6), xyCapVertIndex(7), xyCapVertIndex(5), false);
  }

  if (!clippedX && !clippedZ) {
    // Add a rectangular cap to the negative y and positive y faces of the prism.
    function xzCapVertIndex(prismCorner: number) {
      return prismCorner * cornerVertCount + vertsPerLatitude - 1;
    }
    addQuad(xzCapVertIndex(0), xzCapVertIndex(4), xzCapVertIndex(5), xzCapVertIndex(1), false);
    addQuad(xzCapVertIndex(2), xzCapVertIndex(3), xzCapVertIndex(7), xzCapVertIndex(6), false);
  }

  if (!clippedY && !clippedZ) {
    // Add a rectangular cap to the negative x and positive x faces of the prism.
    function yzCapVertIndex(prismCorner: number) {
      return prismCorner * cornerVertCount;
    }
    addQuad(yzCapVertIndex(0), yzCapVertIndex(2), yzCapVertIndex(6), yzCapVertIndex(4), false);
    addQuad(yzCapVertIndex(1), yzCapVertIndex(5), yzCapVertIndex(7), yzCapVertIndex(3), false);
  }

  if (generateQuadMesh) {
    return quadMesh3New(new Float32Array(verts), new Uint32Array(tris), new Uint32Array(quads));
  } else {
    console.assert(quads.length === 0);
    return mesh3New(new Float32Array(verts), new Uint32Array(tris));
  }
}
